import { useEffect, useState } from "react";
import { useAuthStore, useAppStore, api } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Plus, Trash2, AlertTriangle, RefreshCw, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

const PERIOD_TYPES = [
  { value: "quarter", label: "Тримесечие" },
  { value: "half_year", label: "Полугодие" },
  { value: "year", label: "Годишен" },
];
const QUARTER_OPTS = [
  { value: "1", label: "I тримесечие" },
  { value: "2", label: "II тримесечие" },
  { value: "3", label: "III тримесечие" },
  { value: "4", label: "IV тримесечие" },
];
const HALF_OPTS = [
  { value: "1", label: "I полугодие" },
  { value: "2", label: "II полугодие" },
];

export default function PeriodsPage() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { periods, fetchPeriods } = useAppStore();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [periodType, setPeriodType] = useState("quarter");
  const [periodYear, setPeriodYear] = useState("2025");
  const [periodIndex, setPeriodIndex] = useState("1");
  const [bulkRecalcId, setBulkRecalcId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState("");
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [refTarget, setRefTarget] = useState(null);
  const [selectedRefId, setSelectedRefId] = useState("");

  useEffect(() => { fetchPeriods(); }, []);

  const handleBulkRecalculate = async (periodId) => {
    setBulkRecalcId(periodId);
    setBulkProgress("0/?");
    try {
      const listRes = await api.post(`/periods/${periodId}/bulk-recalculate`);
      const instances = listRes.data.instances;
      const total = instances.length;
      let totalUpdates = 0;
      for (let i = 0; i < instances.length; i++) {
        setBulkProgress(`${i + 1}/${total}`);
        try {
          const res = await api.post(`/instances/${instances[i].id}/deep-recalculate`);
          totalUpdates += res.data.total_updates || 0;
        } catch (err) {
          console.error(`Failed to recalc instance ${instances[i].id}:`, err);
        }
      }
      toast.success(`Масово рекалкулиране завършено: ${total} отчета, ${totalUpdates} обновени клетки`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Грешка при масово рекалкулиране");
    } finally {
      setBulkRecalcId(null);
      setBulkProgress("");
    }
  };

  const handleCreatePeriod = async () => {
    try {
      const payload = {
        period_type: periodType,
        year: parseInt(periodYear),
        period_index: parseInt(periodIndex),
      };
      const res = await api.post("/periods", payload);
      toast.success(`${t("periodCreated")}: ${res.data.display_name}`);
      fetchPeriods();
      setShowCreateDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("failed"));
    }
  };

  const handleDeletePeriod = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/periods/${deleteTarget.id}`);
      toast.success(t("periodDeleted"));
      fetchPeriods();
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("failed"));
    }
  };

  const openRefDialog = (period) => {
    setRefTarget(period);
    // Use "__none__" for empty/null reference_period_id since SelectItem doesn't accept empty string
    setSelectedRefId(period.reference_period_id || "__none__");
    setRefDialogOpen(true);
  };

  const handleSetReference = async () => {
    if (!refTarget) return;
    try {
      // Convert "__none__" back to empty string for API
      const refIdForApi = selectedRefId === "__none__" ? "" : selectedRefId;
      await api.patch(`/periods/${refTarget.id}`, {
        reference_period_id: refIdForApi,
      });
      toast.success(refIdForApi
        ? "Референтният период е зададен"
        : "Референтният период е премахнат");
      fetchPeriods();
      setRefDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("failed"));
    }
  };

  const getRefPeriodName = (refId) => {
    if (!refId) return null;
    const p = periods.find((x) => x.id === refId);
    return p ? (p.display_name || p.name) : null;
  };

  if (!isAdmin()) return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">{t("adminOnly")}</p>
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="periods-title" className="text-3xl font-semibold tracking-tight text-[#14532d]">
            {t("reportingPeriods")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("reportingPeriodsDesc")}</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-period-button" className="h-10 bg-[#14532d] hover:bg-[#166534] text-white">
              <Plus className="w-4 h-4 mr-2" />{t("newPeriod")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("createPeriod")}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>{t("periodType")}</Label>
                <Select value={periodType} onValueChange={(v) => { setPeriodType(v); setPeriodIndex("1"); }}>
                  <SelectTrigger data-testid="period-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIOD_TYPES.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("year")}</Label>
                <Input data-testid="period-year-input" type="number" value={periodYear} onChange={(e) => setPeriodYear(e.target.value)} className="mt-1" />
              </div>
              {periodType !== "year" && (
                <div>
                  <Label>{t("periodIndex")}</Label>
                  <Select value={periodIndex} onValueChange={setPeriodIndex}>
                    <SelectTrigger data-testid="period-index-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(periodType === "quarter" ? QUARTER_OPTS : HALF_OPTS).map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button data-testid="save-period-button" onClick={handleCreatePeriod} className="w-full bg-[#14532d] hover:bg-[#166534] text-white">
                <Plus className="w-4 h-4 mr-2" />{t("createPeriodBtn")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {periods.length === 0 ? (
        <Card className="border-2 border-dashed border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Calendar className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-sm font-medium text-slate-500">{t("noPeriods")}</p>
            <p className="text-xs text-slate-400 mt-1">{t("noPeriodsDesc")}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="py-4 px-6 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#14532d]" />{t("reportingPeriods")} ({periods.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("name")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("periodType")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("year")}</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Референтен период</th>
                  <th className="text-left py-3 px-3 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("date")}</th>
                  <th className="text-right py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => {
                  const refName = getRefPeriodName(p.reference_period_id);
                  return (
                  <tr key={p.id} data-testid={`period-row-${p.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-6 font-medium text-slate-800">{p.display_name || p.name}</td>
                    <td className="py-3 px-3 text-slate-600 text-xs">
                      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                        {p.period_type === "quarter" ? t("quarter") : p.period_type === "half_year" ? t("halfYear") : t("yearly")}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-600">{p.year}</td>
                    <td className="py-3 px-3">
                      {refName ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                          <Link2 className="w-3 h-3" />{refName}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-slate-400 text-xs">{p.start_date} — {p.end_date}</td>
                    <td className="py-3 px-6 text-right space-x-1">
                      <Button data-testid={`set-ref-period-${p.id}`} variant="ghost" size="sm"
                        className="h-7 text-[10px] text-blue-600 hover:bg-blue-50"
                        onClick={() => openRefDialog(p)}
                        title="Задай референтен период">
                        {p.reference_period_id ? <Unlink className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                        <span className="ml-1">{p.reference_period_id ? "Промени реф." : "Задай реф."}</span>
                      </Button>
                      <Button data-testid={`bulk-recalc-period-${p.id}`} variant="ghost" size="sm"
                        className="h-7 text-[10px] text-amber-600 hover:bg-amber-50"
                        disabled={bulkRecalcId !== null}
                        onClick={() => handleBulkRecalculate(p.id)}
                        title={t("bulkRecalcDesc")}>
                        <RefreshCw className={`w-3.5 h-3.5 ${bulkRecalcId === p.id ? "animate-spin" : ""}`} />
                        <span className="ml-1">
                          {bulkRecalcId === p.id ? `${bulkProgress}` : t("bulkRecalculate")}
                        </span>
                      </Button>
                      <Button data-testid={`delete-period-${p.id}`} variant="ghost" size="sm"
                        className="h-7 text-[10px] text-red-500 hover:bg-red-50"
                        onClick={() => { setDeleteTarget(p); setShowDeleteDialog(true); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />{t("deletePeriod")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-700">{t("deletePeriodConfirm")}</p>
            {deleteTarget && <p className="text-sm font-medium">{deleteTarget.display_name || deleteTarget.name}</p>}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteDialog(false)}>{t("close")}</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDeletePeriod}>
                <Trash2 className="w-4 h-4 mr-2" />{t("delete")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reference period dialog */}
      <Dialog open={refDialogOpen} onOpenChange={setRefDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-500" />Референтен период
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              Изберете референтен период за <span className="font-semibold">{refTarget?.display_name || refTarget?.name}</span>.
              Формулите ЗВЕНО с различен шаблон ще търсят данни в референтния период.
            </p>
            <div>
              <Label>Референтен период</Label>
              <Select value={selectedRefId} onValueChange={setSelectedRefId}>
                <SelectTrigger data-testid="ref-period-select" className="mt-1">
                  <SelectValue placeholder="Без референтен период" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Без референтен период</SelectItem>
                  {periods
                    .filter((p) => p.id !== refTarget?.id)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.display_name || p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setRefDialogOpen(false)}>
                Отказ
              </Button>
              <Button data-testid="save-ref-period-button" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSetReference}>
                <Link2 className="w-4 h-4 mr-2" />Запази
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
